import React, { FC, memo } from 'react';
import styled from 'styled-components';
import { gqlMediaCount, gqlProfileTypeEnum as PROFILE_TYPE } from '@minecraft.graphql-types';
import CloudinaryPhoto, { defaultSilhouetteUrl, CloudinaryPhotoProps } from '@minecraft.cloudinary-photo';
import { AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { Directions, ID } from 'components/PackagePresentation/shared/types';
import TalentCardBody, { StyledCustomCardBody } from './TalentCardBody';
import TalentCardFooter from './TalentCardFooter';

type Props = {
  appType: AppType;
  presentationId?: ID;
  skipRepsShortCodes: boolean;
  isPublic;
  isCustomized;
  roleGuid;
  projectGuid;
  roleId;
  submissionId;
  roleName;
  profile;
  profilePhoto;
  snapshotKey;
  showPickButton;
  showImportPhotos;
  disableImportPhotos;
  showFeedback;
  detailLinkPath;
  groupTalentId;
  folderGuid;
  userName;
  pickCount;
  commentCount;
  myPick;
  guid;
  pickedBy;
  handleUpdateSubmission;
  onFeedbackModalOpen;
  menuSettings;
  thumbnailCropSettings?: CloudinaryPhotoProps['thumbnailCropSettings'];
  fastCaptureNotes;
  tooltipPlacement;
  thumbnailSize;
  submissionNote?: string;
  submissionMediaCount?: gqlMediaCount[];
  submissionHasAddInNote;
  handleDeleteModalToggle;
  handleUserNameModalToggle;
  handleViewAddInNotesModalOpen;
  handleEditAddInNotesModalOpen;
  handleQuickViewOverlayToggle: () => void;
  handleReorder?: (direction: Directions) => void;
  handleRoleNameEdit?: () => void;
  renderContactInfo: () => JSX.Element;
  viewOnly: boolean;
  openImportPhotoModal;
  selectionTagCode?: string;
  onSelectionTagChange?: (value: string) => void;
};

const StyledDeletedText = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.5rem;
`;

const CustomStyledCustomCardBody = styled(StyledCustomCardBody)`
  min-height: 238px;
`;

const RenderDeletedProfile = () => {
  const { t } = useTranslation();

  return (
    <CustomStyledCustomCardBody>
      <CloudinaryPhoto fileKey={defaultSilhouetteUrl} customSize={[105, 144]} type="sm" showThumbnail />
      <StyledDeletedText>{t('common:profile.deletedProfile')}</StyledDeletedText>
    </CustomStyledCustomCardBody>
  );
};

const TalentCard: FC<Props> = ({
  appType,
  skipRepsShortCodes,
  isPublic,
  isCustomized,
  submissionId,
  roleId,
  roleGuid,
  projectGuid,
  roleName,
  profile,
  profilePhoto,
  snapshotKey,
  showPickButton,
  showImportPhotos,
  disableImportPhotos,
  showFeedback,
  detailLinkPath,
  groupTalentId,
  presentationId,
  guid,
  folderGuid,
  userName,
  pickCount,
  commentCount,
  myPick,
  pickedBy,
  handleUpdateSubmission,
  onFeedbackModalOpen,
  menuSettings,
  thumbnailCropSettings,
  fastCaptureNotes,
  tooltipPlacement,
  thumbnailSize,
  submissionNote,
  submissionMediaCount,
  submissionHasAddInNote,
  handleDeleteModalToggle,
  handleUserNameModalToggle,
  handleViewAddInNotesModalOpen,
  handleEditAddInNotesModalOpen,
  handleQuickViewOverlayToggle,
  handleReorder,
  handleRoleNameEdit,
  renderContactInfo,
  viewOnly,
  openImportPhotoModal,
  selectionTagCode,
  onSelectionTagChange,
}) => {
  if (!profile?.stageName) {
    return <RenderDeletedProfile />;
  }

  return (
    <>
      <TalentCardBody
        skipRepsShortCodes={skipRepsShortCodes}
        isPublic={isPublic}
        isCustomized={isCustomized}
        isFastCastProfile={profile?.profileType?.code === PROFILE_TYPE.FASTCAST}
        isPersonalProfile={profile?.isPersonal}
        profileId={profile?.id}
        roleId={roleId}
        roleGuid={roleGuid}
        projectGuid={projectGuid}
        roleName={roleName}
        profileMainDivision={profile?.profileMainDivision}
        profileGuid={profile?.guid}
        profileMinor={profile?.minor}
        profileStageName={profile?.stageName}
        profileMainOrganizationName={profile?.profileMainOrganization?.name}
        profileMainDivisionName={profile?.profileMainDivision?.name}
        profilePhoto={profilePhoto}
        profileTalentUnions={profile?.talentUnions}
        profileDateOfBirth={profile?.dateOfBirth}
        snapshotKey={snapshotKey}
        submissionHasAddInNote={submissionHasAddInNote}
        menuSettings={menuSettings}
        thumbnailCropSettings={thumbnailCropSettings}
        tooltipPlacement={tooltipPlacement}
        thumbnailSize={thumbnailSize}
        handleDeleteModalToggle={handleDeleteModalToggle}
        handleViewAddInNotesModalOpen={handleViewAddInNotesModalOpen}
        handleEditAddInNotesModalOpen={handleEditAddInNotesModalOpen}
        handleReorder={handleReorder}
        handleRoleNameEdit={handleRoleNameEdit}
        renderContactInfo={renderContactInfo}
        viewOnly={viewOnly}
      />
      {!viewOnly && (
        <TalentCardFooter
          appType={appType}
          presentationId={presentationId}
          guid={guid}
          isPublic={isPublic}
          showPickButton={showPickButton}
          showImportPhotos={showImportPhotos}
          disableImportPhotos={disableImportPhotos}
          showFeedback={showFeedback}
          submissionId={submissionId}
          profileId={profile?.id}
          groupTalentId={groupTalentId}
          folderGuid={folderGuid}
          userName={userName}
          detailLinkPath={detailLinkPath}
          commentCount={commentCount}
          pickCount={pickCount}
          myPick={myPick}
          pickedBy={pickedBy}
          fastCaptureNotes={fastCaptureNotes}
          submissionNote={submissionNote}
          submissionMediaCount={submissionMediaCount}
          onFeedbackModalOpen={onFeedbackModalOpen}
          handleUpdateSubmission={handleUpdateSubmission}
          handleUserNameModalToggle={handleUserNameModalToggle}
          handleQuickViewOverlayToggle={handleQuickViewOverlayToggle}
          openImportPhotoModal={openImportPhotoModal}
          selectionTagCode={selectionTagCode}
          onSelectionTagChange={onSelectionTagChange}
        />
      )}
    </>
  );
};

export default memo(TalentCard);
